import {api, RequestParam} from "@/shared/api";
import {TaskSelectionConfigurationComponent} from "./taskSelectionConfigurationComponent";
import {TaskDropdown} from "@/shared/task/taskDropdown";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {Task, TerminalConfig, WorkingTimeType} from "@/shared/timr";
import {MobileTaskSelectionType} from "@/shared/timrTypes";
import Modal = BootstrapModal.Modal;

export interface TerminalDialogConfig {
	minuteBasedWorkingTimeTypes: WorkingTimeType[];
}

export class TerminalDialog {

	public static EVENT_SAVED_TERMINAL = "event_saved_terminal";

	private modal: Modal;
	private dom: JQuery;
	private taskDropdown: TaskDropdown;
	private _workingTimeTypeSelect: JQuery;
	private _terminalTaskSelectionConfigSelect: JQuery;
	private taskSelectionConfigurationComponent: TaskSelectionConfigurationComponent;

	constructor(public config: TerminalDialogConfig) {
		this.dom = $("#terminalDialog");
		this.modal = new Modal({
			title: "terminals.dialog.header",
			message: this.dom,
			size: "modal-lg",
			buttons: [
				{
					text: "dialog.save",
					action: (modal, button) => {
						this.saveTerminal();
					},
					css: "btn-primary saveButton"
				},
				{
					text: "dialog.cancel"
				}
			],
		});

		this.initTaskDropdown();
		this.initWorkingTimeTypeSelect(config.minuteBasedWorkingTimeTypes);
	}

	public open(terminal: TerminalConfig) {
		this.dom.find("#terminalDialogTerminalId").val(terminal.id);
		this.dom.find("#terminalDialogDeviceName").val(terminal.name);
		this.dom.find("#terminalDialogTimeoutDuration").val(terminal.timeoutDuration);
		this.dom.find("#useDefaultLocationRequirements").val(terminal.useDefaultLocationRequirements.toString());
		this.dom.find("#geofenceEnabled").val(terminal.geofenceEnabled.toString());
		this.dom.find("#defaultTask.id").val(terminal.defaultTask?.id);
		this.dom.find("#taskSelectionConfig").val(terminal.useMobileTaskSelectionTypes.toString());
		const defaultWorkingTimeType = terminal.defaultWorkingTimeType;

		if (defaultWorkingTimeType) {
			this.workingTimeTypeSelect.val(defaultWorkingTimeType.id)
		} else {
			this.workingTimeTypeSelect.prop('selectedIndex', 0);
		}

		if (terminal.defaultTask) {
			this.taskDropdown.setSelectedTask(terminal.defaultTask);
		}
		if (terminal.qrCodeAuthentication) {
			this.dom.find(".terminalQrCodeAuthentication").prop("checked", "checked");
		} else {
			this.dom.find(".terminalQrCodeAuthentication").prop("checked", false);
		}

		// only initialize the taskSelection configuration if ProjectTime is enabled
		// document.querySelector will return null if ProjectTime is disabled (see terminalDialog.jsp)
		if (globals.projectTimeEnabled) {
			this.taskSelectionConfigurationComponent = document.querySelector("#terminalDialog #taskSelectionConfigurationComponent");
			this.taskSelectionConfigurationComponent.config = {
				availableTaskSelectionTypes: [MobileTaskSelectionType.ALL, MobileTaskSelectionType.BARCODE, MobileTaskSelectionType.LOCATION, MobileTaskSelectionType.RECOMMENDED_AND_RECENT],
				selectedTaskSelectionTypes: terminal.taskSelectionTypes,
				openSearchInAll: terminal.taskSelectionOpenSearchInAll,
				startOnSelection: terminal.taskSelectionStartOnSelection,
				openAutomatically: terminal.taskSelectionOpenAutomatically,
				visible: !terminal.useMobileTaskSelectionTypes
			};
			this.taskSelectionConfigurationComponent.defaultTaskSpecified = !!terminal.defaultTask;
		}

		this.modal.show();
	}

	private saveTerminal() {
		const formData: RequestParam[] = this.dom.find("> form").eq(0).serializeArray();

		if (this.dom.find("select[name=useMobileTaskSelectionTypes]").val() === "false") {
			// add Task Selection Configuration to FormData
			const {
				taskSelectionTypes,
				startOnSelection,
				openSearchInAll,
				openAutomatically
			} = this.taskSelectionConfigurationComponent.getData();
			formData.push({name: "taskSelectionTypes", value: taskSelectionTypes.join()});
			formData.push({name: "taskSelectionStartOnSelection", value: startOnSelection});
			formData.push({name: "taskSelectionOpenSearchInAll", value: openSearchInAll});
			formData.push({name: "taskSelectionOpenAutomatically", value: openAutomatically});
		}
		api("admin/saveTerminalConfig.json")
			.post(formData)
			.done(() => {
				$(this).triggerHandler(TerminalDialog.EVENT_SAVED_TERMINAL);
				this.modal.close();
			})
			.always(() => {
				Button.resetLoading(this.modal.getButtons("saveButton"));
			});
	}

	private initTaskDropdown() {
		this.taskDropdown = new TaskDropdown({
			rootElement: $("#terminalDefaultTaskDropdown"),
			allowSelectionOfNotBookable: false,
			hideInactive: true,
			emptyMessage: messages["terminals.dialog.option.none"]
		}, {
			loadRootNodesURL: "tasks/loadAllTasks.json",
			loadSubNodesURL: "tasks/taskTreeLoad.json",
			filterNodesURL: "tasks/filterTasks.json"
		})

		$(this.taskDropdown).on(TaskDropdown.EVENT_SELECTED, (e, task: Task) => {
			this.taskSelectionConfigurationComponent.defaultTaskSpecified = !!task;
		});
	}

	private initWorkingTimeTypeSelect(workingTimeTypes: WorkingTimeType[]) {
		this.workingTimeTypeSelect.empty();

		this.workingTimeTypeSelect.append($("<option></option>", {value: "null"}).text(messages["terminals.dialog.option.none"]));
		workingTimeTypes.forEach(wtt => {
			this.workingTimeTypeSelect.append($("<option></option>", {value: wtt.id}).text(wtt.name));
		});

		this.workingTimeTypeSelect.on("change", () => {
			const selectValue: string = <string>this.workingTimeTypeSelect.val();
			this.modal.find("input[name='defaultWorkingTimeType\\.id']").val(selectValue === "null" ? null : selectValue);
		});

		this.terminalTaskSelectionConfigSelect.on("change", () => {
			this.taskSelectionConfigurationComponent.changeVisibility(this.terminalTaskSelectionConfigSelect.val() === "false");
		})
	}

	private get workingTimeTypeSelect(): JQuery {
		if (!this._workingTimeTypeSelect) {
			this._workingTimeTypeSelect = $("#terminalDialog #defaultWorkingTimeType");
		}
		return this._workingTimeTypeSelect;
	}

	private get terminalTaskSelectionConfigSelect(): JQuery {
		if (!this._terminalTaskSelectionConfigSelect) {
			this._terminalTaskSelectionConfigSelect = $("#terminalDialog #taskSelectionConfig");
		}
		return this._terminalTaskSelectionConfigSelect;
	}
}
